/**
 * Shared constants for the framework app
 */

// Order statuses that indicate a pending order that can still be canceled
export const CANCELABLE_ORDER_STATUSES = [
  "new",
  "open",
  "pending",
  "created",
  "partially_filled",
] as const;
export type CancelableOrderStatus = (typeof CANCELABLE_ORDER_STATUSES)[number];

// Time-in-force constants for order handling
// IOC (Immediate-or-Cancel) orders fill immediately or cancel - used by Hyperliquid for market orders
export const TIME_IN_FORCE_IOC = "IOC" as const;
export const TIME_IN_FORCE_GTC = "GTC" as const;
export const TIME_IN_FORCE_FOK = "FOK" as const;

// Movement action IDs (matches backend ActionEnum in crypto_lib/models/positions.py)
export const MOVEMENT_ACTION = {
  BUY_SPOT: 1,
  SELL_SPOT: 2,
  BUY_FUTURE: 3,
  SELL_FUTURE: 4,
} as const;

// Helper arrays for checking action types.
// BUY actions always open positions, SELL actions always close them, regardless of direction.
// For futures, both long AND short open with BUY_FUTURE and close with SELL_FUTURE.
// The direction (long/short) is tracked on the position, not the action.
// Note: Explicit readonly number[] type is intentional - without it, `as const` creates a narrow
// tuple type (e.g., readonly [2, 4]) which breaks .includes(number) calls due to TS limitations.
// NOTE: Partial closes are not supported yet. If added, positions will have multiple close
// movements and this simple first=open / last=close model will need rework.
export const EXIT_ACTION_IDS: readonly number[] = [
  MOVEMENT_ACTION.SELL_SPOT,
  MOVEMENT_ACTION.SELL_FUTURE,
];
export const ENTRY_ACTION_IDS: readonly number[] = [
  MOVEMENT_ACTION.BUY_SPOT,
  MOVEMENT_ACTION.BUY_FUTURE,
];

// Polling configuration for execution/job data
export const EXECUTION_POLL_DELAY_MS = 1500;
export const EXECUTION_POLL_MAX_ATTEMPTS = 10; // ~15 seconds for background job polling
export const COMMAND_POLL_MAX_ATTEMPTS = 40; // ~60 seconds for user-initiated command polling
export const END_EXECUTION_POLL_MAX_ATTEMPTS = 40; // ~60 seconds for end execution commands

/** Shown when the API hands a paged list a `next` link it already followed, so paging stopped. */
export const PAGINATION_STALLED_MESSAGE =
  "The server returned a page it already sent, so paging stopped here. Reload to try again.";
